import { useConfirmation } from '@/app/providers';
import { Button, Card } from '@/core/ui/components';
import type { CreateWebhookDto, WebhookConfig, WebhookStatus } from '@yawi/types';
import { WEBHOOK_EVENT_CATEGORIES } from '@yawi/types';
import {
  AlertTriangle,
  CheckCircle,
  Copy,
  Edit2,
  ExternalLink,
  Eye,
  EyeOff,
  History,
  Loader2,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';

// Category labels for UI
const CATEGORY_LABELS: Record<string, string> = {
  access: 'Accesos',
  payments: 'Pagos',
  tickets: 'Tickets',
  reservations: 'Reservaciones',
  guests: 'Invitados',
  users: 'Usuarios',
  announcements: 'Comunicados',
  devices: 'Dispositivos',
};

// Generate secret helper
const generateSecret = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

interface WebhookManagerProps {
  webhooks: WebhookConfig[];
  isLoading: boolean;
  isSaving: boolean;
  onCreateWebhook: (data: CreateWebhookDto) => Promise<WebhookConfig>;
  onUpdateWebhook: (id: string, data: Partial<WebhookConfig>) => Promise<void>;
  onDeleteWebhook: (id: string) => Promise<void>;
  onTestWebhook: (id: string) => Promise<void>;
  onViewDeliveries?: (webhookId: string) => void;
}

export function WebhookManager({
  webhooks,
  isLoading,
  isSaving,
  onCreateWebhook,
  onUpdateWebhook,
  onDeleteWebhook,
  onTestWebhook,
  onViewDeliveries,
}: WebhookManagerProps) {
  const { confirm } = useConfirmation();
  const [showForm, setShowForm] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<WebhookConfig | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [showFormSecret, setShowFormSecret] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateWebhookDto>({
    url: '',
    name: '',
    description: '',
    events: [],
    secret: '',
  });

  const resetForm = () => {
    setFormData({ url: '', name: '', description: '', events: [], secret: '' });
    setEditingWebhook(null);
    setShowForm(false);
    setShowFormSecret(false);
  };

  const handleEdit = (webhook: WebhookConfig) => {
    setEditingWebhook(webhook);
    setFormData({
      url: webhook.url,
      name: webhook.name || '',
      description: webhook.description || '',
      events: webhook.events,
      secret: '', // Don't pre-fill secret on edit
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingWebhook) {
      // For update, only include secret if it was changed
      const updateData = {
        url: formData.url,
        name: formData.name,
        description: formData.description,
        events: formData.events,
        ...(formData.secret ? { secret: formData.secret } : {}),
      };
      await onUpdateWebhook(editingWebhook.id, updateData);
    } else {
      await onCreateWebhook(formData);
    }
    resetForm();
  };

  const handleGenerateSecret = () => {
    setFormData(prev => ({ ...prev, secret: generateSecret() }));
  };

  const handleDelete = async (webhook: WebhookConfig) => {
    const confirmed = await confirm({
      title: 'Eliminar Webhook',
      message: `¿Estás seguro de eliminar el webhook "${webhook.name || 'Sin nombre'}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (confirmed) {
      await onDeleteWebhook(webhook.id);
    }
  };

  const handleToggleStatus = async (webhook: WebhookConfig) => {
    const newStatus: WebhookStatus = webhook.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    await onUpdateWebhook(webhook.id, { status: newStatus });
  };

  const toggleEventSelection = (event: string) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event],
    }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleString();
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    if (formData.events.length === 0) return false;
    if (!formData.url) return false;
    // Secret is required for new webhooks, optional for edits
    if (!editingWebhook && formData.secret.length < 16) return false;
    if (editingWebhook && formData.secret && formData.secret.length < 16) return false;
    return true;
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 animate-spin text-brand-500" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-800 dark:text-white/90">
            Webhooks
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Recibe notificaciones en tiempo real cuando ocurran eventos
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setShowForm(true)}
        >
          Agregar Webhook
        </Button>
      </div>

      {/* Webhook List */}
      {webhooks.length === 0 && !showForm ? (
        <Card className="p-6">
          <div className="text-center py-12">
            <ExternalLink className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 dark:text-white/90">
              No hay webhooks configurados
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1 mb-4">
              Agrega un webhook para recibir notificaciones de eventos
            </p>
            <Button
              variant="primary"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowForm(true)}
            >
              Agregar Tu Primer Webhook
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <Card key={webhook.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h4 className="font-medium text-gray-800 dark:text-white/90">
                      {webhook.name || 'Sin nombre'}
                    </h4>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      webhook.status === 'ACTIVE'
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : webhook.status === 'FAILED'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {webhook.status}
                    </span>
                    {webhook.failureCount > 0 && (
                      <span className="flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full">
                        <AlertTriangle className="w-3 h-3" />
                        {webhook.failureCount} fallos
                      </span>
                    )}
                  </div>

                  {webhook.description && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {webhook.description}
                    </p>
                  )}

                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-xs">
                      {webhook.url}
                    </code>
                    <button
                      onClick={() => copyToClipboard(webhook.url)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1">
                    {webhook.events.map((event) => (
                      <span
                        key={event}
                        className="px-2 py-0.5 text-xs bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400 rounded"
                      >
                        {event}
                      </span>
                    ))}
                  </div>

                  <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <span>Último trigger: {formatDate(webhook.lastTriggeredAt)}</span>
                    {webhook.lastSuccessAt && (
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-emerald-500" />
                        Éxito: {formatDate(webhook.lastSuccessAt)}
                      </span>
                    )}
                    {webhook.lastFailureAt && (
                      <span className="flex items-center gap-1">
                        <XCircle className="w-3 h-3 text-red-500" />
                        Fallo: {formatDate(webhook.lastFailureAt)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {onViewDeliveries && (
                    <button
                      onClick={() => onViewDeliveries(webhook.id)}
                      className="p-2 text-gray-400 hover:text-brand-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                      title="Ver entregas"
                    >
                      <History className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => onTestWebhook(webhook.id)}
                    className="p-2 text-gray-400 hover:text-brand-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                    title="Enviar webhook de prueba"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(webhook)}
                    className="p-2 text-gray-400 hover:text-brand-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                    title="Editar webhook"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(webhook)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                    title="Eliminar webhook"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingWebhook ? 'Editar Webhook' : 'Crear Webhook'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  URL del Endpoint <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://api.example.com/webhook"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Mi Webhook de Pagos"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descripción
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Notifica cuando se recibe un pago"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              {/* Secret */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Secret {!editingWebhook && <span className="text-red-500">*</span>}
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showFormSecret ? 'text' : 'password'}
                      value={formData.secret}
                      onChange={(e) => setFormData(prev => ({ ...prev, secret: e.target.value }))}
                      placeholder={editingWebhook ? 'Dejar vacío para mantener el actual' : 'Mínimo 16 caracteres'}
                      required={!editingWebhook}
                      minLength={editingWebhook ? undefined : 16}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                               focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowFormSecret(!showFormSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showFormSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateSecret}
                    leftIcon={<RefreshCw className="w-4 h-4" />}
                  >
                    Generar
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Este secreto se usará para firmar los payloads del webhook
                </p>
              </div>

              {/* Events */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Eventos <span className="text-red-500">*</span>
                </label>
                <div className="space-y-4 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  {Object.entries(WEBHOOK_EVENT_CATEGORIES).map(([category, events]) => (
                    <div key={category}>
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                        {CATEGORY_LABELS[category] || category}
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {events.map(({ value, label }) => (
                          <label
                            key={value}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.events.includes(value)}
                              onChange={() => toggleEventSelection(value)}
                              className="w-4 h-4 text-brand-500 rounded border-gray-300 focus:ring-brand-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {formData.events.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Selecciona al menos un evento</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={resetForm}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  disabled={isSaving || !isFormValid()}
                  leftIcon={isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                >
                  {editingWebhook ? 'Actualizar' : 'Crear'} Webhook
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
